/**
 * Phase 6 Tier 1 (Feb 2026) — Mini SVG sparkline.
 *
 * Used inside the Cohort Revenue tile to show daily signups + daily revenue
 * across the active filter window. CSS-only animations, zero deps.
 *
 * Usage:
 *   <MiniSparkline data={[2, 0, 5, 8, 3]} color="#39FF14" />
 *
 * Renders as a smooth polyline filling the available width. Auto-scales to
 * the max value; renders a flat baseline when all values are zero.
 */
import { useMemo } from 'react';

export function MiniSparkline({
  data,
  color = '#39FF14',
  height = 32,
  width = 200,
  ariaLabel,
}) {
  const { points, areaPath, max, hasData } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return { points: '', areaPath: '', max: 0, hasData: false };
    }
    const n = data.length;
    const max = Math.max(...data, 1); // avoid /0 — flat line when all zero
    const hasData = data.some((v) => v > 0);
    const stepX = n === 1 ? width / 2 : width / (n - 1);
    const baselineY = height - 2;
    const usableH = height - 4;
    const pts = data.map((v, i) => {
      const x = i * stepX;
      const y = baselineY - (v / max) * usableH;
      return [x, y];
    });
    const points = pts.map(([x, y]) => `${Number(x ?? 0).toFixed(1)},${Number(y ?? 0).toFixed(1)}`).join(' ');
    // Build a closed polygon for the under-line fill
    const areaPath = [
      `M 0,${baselineY}`,
      ...pts.map(([x, y]) => `L ${Number(x ?? 0).toFixed(1)},${Number(y ?? 0).toFixed(1)}`),
      `L ${(n === 1 ? width / 2 : width).toFixed(1)},${baselineY}`,
      'Z',
    ].join(' ');
    return { points, areaPath, max, hasData };
  }, [data, height, width]);

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel || 'Trend sparkline'}
      style={{ display: 'block' }}
    >
      {hasData && (
        <>
          <path d={areaPath} fill={color} opacity="0.15" />
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* Final-point dot for emphasis */}
          {data.length > 0 && (() => {
            const last = data[data.length - 1];
            const lastX = data.length === 1 ? width / 2 : width;
            const lastY = (height - 2) - (last / max) * (height - 4);
            return <circle cx={lastX} cy={lastY} r="2" fill={color} />;
          })()}
        </>
      )}
      {!hasData && (
        <line
          x1="0" x2={width}
          y1={height - 2} y2={height - 2}
          stroke="#2A0F3D" strokeWidth="1" strokeDasharray="3 3"
        />
      )}
    </svg>
  );
}
